import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type OfficeNotification from '../models/office-notification';
import MessageCard from './message-card.component';
import EditOfficeNotificationModal from '../modals/edit-office-notification-moal.component';
import { momentLabel, notificationStatus, windowLabel } from '../message-status';

interface Props {
  readonly initialNotification: OfficeNotification;
  readonly handleUpdateNotification: (
    originalNotification: OfficeNotification,
    changedNotification: OfficeNotification
  ) => Promise<boolean>;
  readonly notificationIsValid: (value: OfficeNotification) => boolean;
  readonly onDelete: (value: OfficeNotification) => Promise<boolean>;
}

const OfficeNotificationCard = ({
  initialNotification,
  handleUpdateNotification,
  notificationIsValid,
  onDelete,
}: Props): React.JSX.Element => {
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  const { t } = useTranslation();
  const translate = (key: string): string => t(key) ?? '';

  const status = notificationStatus(initialNotification.scheduledDateTime, initialNotification.expirationDate);

  // A recurring notification keeps firing until it expires, so it gets the window wording; a
  // one-off is a single moment in time.
  const when =
    initialNotification.expirationDate != null
      ? windowLabel(status, initialNotification.scheduledDateTime, initialNotification.expirationDate, translate)
      : momentLabel(initialNotification.scheduledDateTime, translate);

  return (
    <>
      <MessageCard
        status={status}
        title={initialNotification.textDutch}
        body={initialNotification.textEnglish}
        when={when}
        tagLabel={initialNotification.isRecurring ? translate('recurring') : undefined}
        onOpen={(): void => {
          setShowNotificationModal(true);
        }}
      />
      <EditOfficeNotificationModal
        showModal={showNotificationModal}
        initialNotification={initialNotification}
        handleSave={handleUpdateNotification}
        onDelete={onDelete}
        handleClose={(): void => {
          setShowNotificationModal(false);
        }}
        notificationIsValid={notificationIsValid}
      />
    </>
  );
};

export default OfficeNotificationCard;
